import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import { NodeSelection, PluginKey, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { CellSelection } from '@atlaskit/editor-tables';
import { fg } from '@atlaskit/platform-feature-flags';

import type { MalleableUiPlugin, PluginConfiguration } from '../../malleableUiPluginType';

import {
	createRemixThinkingWidgetDecoration,
	getRemixThinkingWidgetContainer,
	renderRemixThinkingPortal,
} from './remix-thinking-decoration';

const THINKING_HIGHLIGHT_CLASS = 'remix-thinking-highlight';

export type SavedSelection = {
	anchorCellPos?: number;
	from: number;
	headCellPos?: number;
	isNodeSelection: boolean;
	to: number;
};

export type RemixThinkingPluginState = {
	contentType?: string;
	decorationSet: DecorationSet;
	isComplete?: boolean;
	isThinking: boolean;
	savedSelection?: SavedSelection;
};

export type RemixThinkingPluginMeta = {
	anchorCellPos?: number;
	cellSelectionRanges?: Array<{ from: number; to: number }>;
	contentType?: string;
	headCellPos?: number;
	isComplete?: boolean;
	isNodeSelection?: boolean;
	isThinking?: boolean;
	preserveSavedSelection?: boolean;
	thinkingSelectionFrom?: number;
	thinkingSelectionTo?: number;
};

export const remixThinkingPluginKey: PluginKey<RemixThinkingPluginState> =
	new PluginKey<RemixThinkingPluginState>('remixThinkingPlugin');

export function createRemixThinkingPmPlugin({
	api,
	config,
	dispatch,
	getIntl,
	nodeViewPortalProviderAPI,
}: {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	config: PluginConfiguration;
	dispatch: Dispatch;
	getIntl: PMPluginFactoryParams['getIntl'];
	nodeViewPortalProviderAPI: PortalProviderAPI;
}): SafePlugin {
	return new SafePlugin({
		key: remixThinkingPluginKey,
		state: {
			init(): RemixThinkingPluginState {
				return {
					isThinking: false,
					isComplete: false,
					decorationSet: DecorationSet.empty,
				};
			},
			apply(tr, pluginState, _oldState, newState): RemixThinkingPluginState {
				const meta: RemixThinkingPluginMeta | undefined = tr.getMeta(remixThinkingPluginKey);

				if (meta) {
					if (
						meta.isThinking === true &&
						meta.thinkingSelectionFrom !== undefined &&
						meta.thinkingSelectionTo !== undefined
					) {
						const from = meta.thinkingSelectionFrom;
						const to = meta.thinkingSelectionTo;
						let decorations: Decoration[];

						if (meta.cellSelectionRanges) {
							decorations = meta.cellSelectionRanges.map((range) =>
								Decoration.inline(range.from, range.to, { class: THINKING_HIGHLIGHT_CLASS }),
							);
						} else if (meta.isNodeSelection) {
							decorations = [Decoration.node(from, to, { class: THINKING_HIGHLIGHT_CLASS })];
						} else {
							decorations = [Decoration.inline(from, to, { class: THINKING_HIGHLIGHT_CLASS })];
						}

						if (fg('platform_editor_ai_remix_in_editor_loading')) {
							decorations.push(
								createRemixThinkingWidgetDecoration({
									api,
									contentType: meta.contentType,
									doc: newState.doc,
									getIntl,
									isComplete: false,
									nodeViewPortalProviderAPI,
									to,
								}),
							);
						}

						const decorationSet = DecorationSet.create(newState.doc, decorations);
						const result: RemixThinkingPluginState = {
							contentType: fg('platform_editor_ai_remix_in_editor_loading')
								? meta.contentType
								: undefined,
							isThinking: true,
							isComplete: fg('platform_editor_ai_remix_in_editor_loading') ? false : undefined,
							decorationSet,
							savedSelection: {
								from,
								to,
								isNodeSelection: meta.isNodeSelection ?? false,
								anchorCellPos: meta.anchorCellPos,
								headCellPos: meta.headCellPos,
							},
						};
						dispatch(remixThinkingPluginKey, result);
						return result;
					}

					if (meta.isComplete === true && fg('platform_editor_ai_remix_in_editor_loading')) {
						if (pluginState.isComplete || !pluginState.isThinking) {
							return pluginState;
						}

						const result: RemixThinkingPluginState = {
							contentType: pluginState.contentType,
							isThinking: pluginState.isThinking,
							isComplete: true,
							decorationSet: pluginState.decorationSet,
							savedSelection: pluginState.savedSelection,
						};
						dispatch(remixThinkingPluginKey, result);
						return result;
					}

					if (meta.isThinking === false) {
						const result: RemixThinkingPluginState = {
							contentType: fg('platform_editor_ai_remix_in_editor_loading')
								? pluginState.contentType
								: undefined,
							isThinking: false,
							isComplete: fg('platform_editor_ai_remix_in_editor_loading') ? false : undefined,
							decorationSet: DecorationSet.empty,
							savedSelection: meta.preserveSavedSelection ? pluginState.savedSelection : undefined,
						};
						dispatch(remixThinkingPluginKey, result);
						return result;
					}
				}

				if (pluginState.isThinking && pluginState.decorationSet !== DecorationSet.empty) {
					const mappedDecorationSet = pluginState.decorationSet.map(tr.mapping, newState.doc);
					if (mappedDecorationSet !== pluginState.decorationSet) {
						const mappedSavedSelection = pluginState.savedSelection
							? {
									...pluginState.savedSelection,
									from: tr.mapping.map(pluginState.savedSelection.from),
									to: tr.mapping.map(pluginState.savedSelection.to),
									anchorCellPos:
										pluginState.savedSelection.anchorCellPos !== undefined
											? tr.mapping.map(pluginState.savedSelection.anchorCellPos)
											: undefined,
									headCellPos:
										pluginState.savedSelection.headCellPos !== undefined
											? tr.mapping.map(pluginState.savedSelection.headCellPos)
											: undefined,
								}
							: undefined;
						const result: RemixThinkingPluginState = {
							...pluginState,
							decorationSet: mappedDecorationSet,
							savedSelection: mappedSavedSelection,
						};
						dispatch(remixThinkingPluginKey, result);
						return result;
					}
				}

				return pluginState;
			},
		},

		view() {
			return {
				update(view, previousState) {
					const previousPluginState = remixThinkingPluginKey.getState(previousState);
					const currentPluginState = remixThinkingPluginKey.getState(view.state);

					if (
						currentPluginState?.isThinking &&
						!previousPluginState?.isComplete &&
						currentPluginState.isComplete &&
						fg('platform_editor_ai_remix_in_editor_loading')
					) {
						const container = getRemixThinkingWidgetContainer(view);
						if (!container) {
							return;
						}

						renderRemixThinkingPortal({
							api,
							container,
							contentType: currentPluginState.contentType,
							getIntl,
							isComplete: currentPluginState.isComplete ?? false,
							nodeViewPortalProviderAPI,
						});
					}
				},
			};
		},

		appendTransaction(_transactions, oldState, newState) {
			const oldPluginState = remixThinkingPluginKey.getState(oldState);
			const newPluginState = remixThinkingPluginKey.getState(newState);

			if (
				oldPluginState?.isThinking &&
				!newPluginState?.isThinking &&
				newPluginState?.savedSelection
			) {
				const { savedSelection } = newPluginState;
				const tr = newState.tr;

				if (
					savedSelection.anchorCellPos !== undefined &&
					savedSelection.headCellPos !== undefined
				) {
					tr.setSelection(
						CellSelection.create(tr.doc, savedSelection.anchorCellPos, savedSelection.headCellPos),
					);
				} else if (savedSelection.isNodeSelection) {
					tr.setSelection(NodeSelection.create(tr.doc, savedSelection.from));
				} else {
					tr.setSelection(TextSelection.create(tr.doc, savedSelection.from, savedSelection.to));
				}

				return tr;
			}

			return null;
		},
		props: {
			decorations(state) {
				return remixThinkingPluginKey.getState(state)?.decorationSet ?? DecorationSet.empty;
			},
		},
	});
}
